import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import { Minus, Plus, X, ShoppingBag, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CartDrawer = () => {
  const {
    cartItems,
    isCartOpen,
    setIsCartOpen,
    removeFromCart,
    updateQuantity,
    cartTotal,
    loading,
  } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate('/checkout');
  };

  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetContent className="bg-[#FDF6E3] border-l-[#D4A853]/30 w-full sm:max-w-md flex flex-col">
        <SheetHeader className="border-b border-[#D4A853]/30 pb-4">
          <SheetTitle className="flex items-center gap-3 text-[#5C3D2E] font-serif text-xl">
            <ShoppingBag className="h-6 w-6 text-[#C17817]" />
            Your Cart ({cartItems.length})
          </SheetTitle>
        </SheetHeader>

        {loading && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10">
            <Loader2 className="h-8 w-8 animate-spin text-[#C17817]" />
          </div>
        )}

        {cartItems.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <div className="w-24 h-24 rounded-full bg-[#C17817]/10 flex items-center justify-center">
              <ShoppingBag className="h-12 w-12 text-[#C17817]/50" />
            </div>
            <p className="text-[#5C3D2E]/70 text-center">
              Your cart is empty.<br />Start adding some heritage goodness!
            </p>
            <Button
              onClick={() => {
                setIsCartOpen(false);
                navigate('/products');
              }}
              className="bg-[#C17817] hover:bg-[#8B4513] text-[#FDF6E3] mt-4"
            >
              Browse Products
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto py-4 space-y-4">
              {cartItems.map((item) => (
                <div
                  key={item.product_id}
                  className="flex gap-4 p-3 bg-white/50 rounded-xl border border-[#D4A853]/20"
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium text-[#5C3D2E] text-sm leading-tight">
                          {item.name}
                        </h4>
                        <p className="text-xs text-[#8B6914] mt-1">{item.weight}</p>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.product_id)}
                        className="text-[#5C3D2E]/50 hover:text-red-500 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center gap-2 bg-[#FDF6E3] rounded-lg border border-[#D4A853]/30">
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                          className="p-1.5 hover:bg-[#C17817]/10 rounded-l-lg transition-colors"
                        >
                          <Minus className="h-3 w-3 text-[#5C3D2E]" />
                        </button>
                        <span className="px-2 text-sm font-medium text-[#5C3D2E]">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          className="p-1.5 hover:bg-[#C17817]/10 rounded-r-lg transition-colors"
                        >
                          <Plus className="h-3 w-3 text-[#5C3D2E]" />
                        </button>
                      </div>
                      <span className="font-semibold text-[#C17817]">
                        ${(item.price * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[#D4A853]/30 pt-4 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-[#5C3D2E]/70">Subtotal</span>
                <span className="text-lg font-semibold text-[#5C3D2E]">
                  ${cartTotal.toFixed(2)}
                </span>
              </div>
              <p className="text-xs text-[#5C3D2E]/50 text-center">
                Shipping calculated at checkout
              </p>
              <Button
                onClick={handleCheckout}
                className="w-full bg-[#C17817] hover:bg-[#8B4513] text-[#FDF6E3] py-6 text-base font-medium shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Proceed to Checkout
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
